import React from 'react';
import { Search, Handshake, Home, CheckCircle, ArrowRight } from 'lucide-react';
import brandConfig from '../../config/brandConfig';

const HowItWorks = () => {
  const dashboardUrl = brandConfig.dashboardUrl;

  const steps = [
    {
      icon: <Search className="w-7 h-7" />,
      number: "01",
      title: "Busca tu lote ideal",
      description: "Usa nuestros filtros avanzados para encontrar el lote perfecto segun tu ubicacion, presupuesto y necesidades.",
      gradient: "from-blue-500 to-cyan-500",
      accentColor: "#0ea5e9"
    },
    {
      icon: <Handshake className="w-7 h-7" />,
      number: "02",
      title: "Conecta con inmobiliarias",
      description: "Te ponemos en contacto directo con las mejores inmobiliarias certificadas de Latinoamerica.",
      gradient: "from-emerald-500 to-green-500",
      accentColor: "#10b981"
    },
    {
      icon: <Home className="w-7 h-7" />,
      number: "03",
      title: "Visita y evalua",
      description: "Programa visitas a los lotes que mas te interesen y evalua todas las opciones con ayuda de expertos.",
      gradient: "from-amber-500 to-orange-500",
      accentColor: "#f59e0b"
    },
    {
      icon: <CheckCircle className="w-7 h-7" />,
      number: "04",
      title: "Compra con confianza",
      description: "Realiza tu compra con total seguridad y respaldo legal. Te acompanamos en todo el proceso.",
      gradient: "from-violet-500 to-purple-500",
      accentColor: "#8b5cf6"
    }
  ];

  return (
    <div className="py-24 lg:py-32 bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-20">
          <p className="text-sm font-bold uppercase tracking-[0.2em] mb-3" style={{ color: brandConfig.primaryColor }}>
            Proceso Simple
          </p>
          <h2 className="text-4xl lg:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 tracking-tight">
            Como funciona
            <span className="block font-display italic mt-2" style={{ color: brandConfig.primaryColor }}>{brandConfig.domain}?</span>
          </h2>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Un proceso simple y seguro para encontrar tu lote ideal
          </p>
        </div>

        {/* Steps - horizontal timeline on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {steps.map((step, index) => (
            <div key={index} className="relative group">
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-10 left-[calc(50%+40px)] right-[-calc(50%-40px)] w-full h-px bg-gradient-to-r from-gray-200 dark:from-gray-700 to-transparent z-0" />
              )}

              <div className="relative bg-gray-50 dark:bg-gray-800/50 rounded-3xl p-8 border border-gray-100 dark:border-gray-800 card-hover text-center">
                {/* Step number */}
                <div className="text-6xl font-extrabold mb-4 opacity-[0.06] dark:opacity-[0.04] absolute top-4 right-6" style={{ color: step.accentColor }}>
                  {step.number}
                </div>

                <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br ${step.gradient} rounded-2xl mb-6 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {step.icon}
                </div>
                <h3 className="text-lg font-extrabold text-gray-900 dark:text-white mb-3 tracking-tight">
                  {step.title}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Inmobiliaria CTA */}
        <div className="bg-gradient-to-br from-gray-50 to-orange-50/50 dark:from-gray-800 dark:to-gray-800 rounded-3xl p-8 lg:p-12 border border-gray-100 dark:border-gray-800">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl lg:text-3xl font-extrabold text-gray-900 dark:text-white mb-4 tracking-tight">
                Eres inmobiliaria?
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
                Unete a nuestra plataforma y llega a miles de clientes potenciales.
                Ofrecemos herramientas avanzadas para gestionar tus propiedades y
                aumentar tus ventas.
              </p>
              <a
                href={`${dashboardUrl}/iniciar-sesion`}
                className="inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-xl font-bold transition-all duration-300 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 hover:scale-[1.02]"
                style={{ background: brandConfig.getPrimaryGradient() }}
              >
                Registra tu inmobiliaria
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 text-center border border-gray-100 dark:border-gray-700 shadow-lg">
              <div className="text-5xl font-extrabold mb-2 tracking-tight" style={{ color: brandConfig.primaryColor }}>100%</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white mb-1">Gratis</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Para inmobiliarias registradas
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
